import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function RemindersTab({ userId, reminders, refreshData }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminder_type: 'medication',
    date: '',
    time: '',
    repeat: 'none'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date || !formData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const scheduledDateTime = new Date(`${formData.date}T${formData.time}:00Z`);
      
      const payload = {
        user_id: userId,
        title: formData.title,
        description: formData.description || null,
        reminder_type: formData.reminder_type,
        scheduled_time: scheduledDateTime.toISOString(),
        repeat: formData.repeat !== 'none' ? formData.repeat : null
      };

      await axios.post(`${API}/reminders`, payload);
      toast.success('Reminder created successfully!');
      setIsDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        reminder_type: 'medication',
        date: '',
        time: '',
        repeat: 'none'
      });
      await refreshData();
    } catch (error) {
      console.error('Error creating reminder:', error);
      toast.error('Failed to create reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      await refreshData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleAcknowledge = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}/acknowledge`);
      toast.success('Reminder marked as completed');
      await refreshData();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'default',
      completed: 'secondary',
      missed: 'destructive'
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  return (
    <div data-testid="reminders-tab">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Manage Reminders</h3>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-reminder-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Reminder
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="add-reminder-dialog">
            <DialogHeader>
              <DialogTitle>Create New Reminder</DialogTitle>
              <DialogDescription>
                Set up a reminder for medication, routines, or appointments
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  data-testid="reminder-title-input"
                  id="title"
                  placeholder="e.g., Take blood pressure medication"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  data-testid="reminder-description-input"
                  id="description"
                  placeholder="Additional details (optional)"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="type">Type</Label>
                <Select
                  value={formData.reminder_type}
                  onValueChange={(value) => setFormData({ ...formData, reminder_type: value })}
                >
                  <SelectTrigger data-testid="reminder-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medication">Medication</SelectItem>
                    <SelectItem value="routine">Routine</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    data-testid="reminder-date-input"
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="time">Time *</Label>
                  <Input
                    data-testid="reminder-time-input"
                    id="time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="repeat">Repeat</Label>
                <Select
                  value={formData.repeat}
                  onValueChange={(value) => setFormData({ ...formData, repeat: value })}
                >
                  <SelectTrigger data-testid="reminder-repeat-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Repeat</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button data-testid="submit-reminder-btn" type="submit" className="w-full">
                Create Reminder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {reminders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No reminders yet. Create one to get started.</p>
        </div>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-3">
            {reminders.map((reminder) => (
              <Card key={reminder.id} data-testid={`reminder-card-${reminder.id}`}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{reminder.title}</h4>
                          {reminder.description && (
                            <p className="text-sm text-gray-600 mt-1">{reminder.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(reminder.scheduled_time).toLocaleString()}
                            </span>
                            <Badge variant="outline">{reminder.reminder_type}</Badge>
                            {getStatusBadge(reminder.status)}
                            {reminder.repeat && (
                              <Badge variant="secondary">Repeats {reminder.repeat}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {reminder.status === 'pending' && (
                        <Button
                          data-testid={`acknowledge-reminder-btn-${reminder.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleAcknowledge(reminder.id)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        data-testid={`delete-reminder-btn-${reminder.id}`}
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(reminder.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}